import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { CalculationRuleSummary } from 'entities/tag';
import { buildCalcTree, CalcNode, CalcTreeByElr, resolveConceptLabel, formatDefinition } from 'app/utils/tag-utils';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { Typography } from 'app/components/ui';

interface CalculationPaneProps {
  rules: CalculationRuleSummary[] | undefined;
  selectedNodeKey: string | null;
  onSelect: (conceptName: string, nodeKey?: string) => void;
}

const weightSign = (weight: number | null): string => {
  if (weight === null) return '';
  return weight < 0 ? '−' : '+';
};

interface CalcTreeNodeProps {
  node: CalcNode;
  lang: string;
  level: number;
  nodeKey: string;
  selectedNodeKey: string | null;
  onSelect: (conceptName: string, nodeKey?: string) => void;
}

const CalcTreeNode = ({ node, lang, level, nodeKey, selectedNodeKey, onSelect }: CalcTreeNodeProps) => {
  const { t } = useTranslation('tag');

  const [isOpen, setIsOpen] = useState(false);
  const label = resolveConceptLabel({ languages: node.concept.labels, currentLang: lang }) || node.concept.name;
  const hasChildren = node.children.length > 0;

  const card = (
    <div className={`taxonomy__list-item tree-overview__calc-card${selectedNodeKey === nodeKey ? ' selected' : ''}`}>
      {node.weight !== null && <span className="tree-overview__calc-weight">{weightSign(node.weight)}</span>}
      <Typography variant="body-5">{label}</Typography>
      {node.concept.isExtension && (
        <span className="taxonomy__tag taxonomy__tag--extension" role="status">
          {t('extension')}
        </span>
      )}
    </div>
  );

  if (hasChildren) {
    return (
      <li className="pos-rel">
        {isOpen && <span className="taxonomy__dotted-line taxonomy__dotted-line--with-children" />}
        <div className="taxonomy__list-wrapper">
          <span className="taxonomy__prefix-line" />
          <div
            className="flex-row cursor-pointer"
            onClick={() => {
              setIsOpen((o) => !o);
              onSelect(node.concept.name, nodeKey);
            }}
          >
            <div className={`taxonomy__icon ${isOpen ? '' : 'icon-closed'}`}>
              <i className="fa-solid fa-caret-down" />
            </div>
            {card}
          </div>
        </div>
        {isOpen && (
          <ul className="taxonomy__child-wrapper child-indent pos-rel">
            {node.children.map((child, idx) => (
              <CalcTreeNode
                key={`${child.concept.id}|${idx}`}
                node={child}
                lang={lang}
                level={level + 1}
                nodeKey={`${nodeKey}>${child.concept.id}|${idx}`}
                selectedNodeKey={selectedNodeKey}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li className="pos-rel">
      <div className="taxonomy__list-wrapper">
        <span className={level > 1 ? 'taxonomy__prefix-line large' : 'taxonomy__prefix-line'} />
        <div className="flex-row cursor-pointer" onClick={() => onSelect(node.concept.name, nodeKey)}>
          {card}
        </div>
      </div>
    </li>
  );
};

const CalcGroup = ({
  group,
  groupKey,
  lang,
  selectedNodeKey,
  onSelect,
}: {
  group: CalcTreeByElr[number];
  groupKey: string;
  lang: string;
  selectedNodeKey: string | null;
  onSelect: (conceptName: string, nodeKey?: string) => void;
}) => {
  const { t } = useTranslation('tag');

  const [isOpen, setIsOpen] = useState(false);
  const { category, label } = formatDefinition(group.roleDefinition ?? group.linkroleUri ?? '');
  const isFilerGroup = !group.linkroleUri;

  return (
    <ul className={`taxonomy ${isOpen ? 'border-gray-300' : ''}`}>
      <li
        className={`taxonomy__list-item ${isOpen ? 'bg-custom-dark border-bottom' : 'bg-white'}${isFilerGroup ? ' tree-overview__calc-filer-header' : ''}`}
        onClick={() => setIsOpen((o) => !o)}
      >
        <div className={`taxonomy__icon ${isOpen ? '' : 'icon-closed'}`}>
          <i className="fa-solid fa-caret-down" />
        </div>
        <div>
          {isFilerGroup ? (
            <Typography variant="body-7">{t('treeOverview.calc.filerGroup')}</Typography>
          ) : (
            <>
              <Typography variant="body-3">{category}</Typography>
              <Typography variant="body-7">{label}</Typography>
            </>
          )}
        </div>
      </li>
      {isOpen && (
        <ul className="taxonomy__child-wrapper pr-8">
          {group.roots.map((root, rIdx) => (
            <CalcTreeNode
              key={`${root.concept.id}|${rIdx}`}
              node={root}
              lang={lang}
              level={1}
              nodeKey={`${groupKey}>${root.concept.id}|${rIdx}`}
              selectedNodeKey={selectedNodeKey}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </ul>
  );
};

export const CalculationPane = ({ rules, selectedNodeKey, onSelect }: CalculationPaneProps) => {
  const { t } = useTranslation('tag');

  const { tagLanguage } = useTagDisplay();
  const tree = useMemo(() => buildCalcTree(rules ?? []), [rules]);

  if (tree.length === 0) {
    return <div className="tree-overview__pane-empty">{t('treeOverview.empty.calculation')}</div>;
  }

  return (
    <>
      {tree.map((group, idx) => {
        const groupKey = group.linkroleUri ?? `group-${idx}`;
        return (
          <CalcGroup key={`${groupKey}|${idx}`} group={group} groupKey={groupKey} lang={tagLanguage} selectedNodeKey={selectedNodeKey} onSelect={onSelect} />
        );
      })}
    </>
  );
};
